
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { getDomainById, updateDomain } from '../services/storageService';
import { Domain, GdprSettings as GdprSettingsType } from '../types';
import { 
  Loader2, Save, ShieldCheck, Sun, Moon, AlignLeft, AlignCenter, AlignRight, Share2, 
  Globe, Link as LinkIcon, Code, X, Copy, Check, Accessibility, EyeOff
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { EmbedModal } from '../components/EmbedModal';

const DEFAULT_SETTINGS: GdprSettingsType = {
  style: 'light',
  position: 'bottom-left',
  messageText: 'We use cookies to analyze website traffic and improve your experience.',
  acceptButtonText: 'Accept',
  declineButtonText: 'Decline',
};

// --- Sub Components ---

const GdprBannerPreview: React.FC<{ settings: GdprSettingsType, enabled: boolean }> = ({ settings, enabled }) => {
  const isDark = settings.style === 'dark';
  const positionClasses = {
    'bottom-left': 'left-4',
    'bottom-right': 'right-4',
    'bottom-center': 'left-1/2 -translate-x-1/2 max-w-lg w-[calc(100%-32px)] sm:w-auto',
  };
  
  return (
    <div 
      className={`absolute bottom-4 ${positionClasses[settings.position]}
        p-4 rounded-xl shadow-2xl flex items-center justify-between gap-4 
        transition-all duration-300 border
        ${!enabled ? 'opacity-20 grayscale pointer-events-none' : 'opacity-100'}
        ${isDark 
          ? 'bg-slate-800 text-slate-200 border-slate-700' 
          : 'bg-white text-slate-700 border-slate-200'}`
      }
    >
      <p className="text-sm">{settings.messageText}</p>
      <div className="flex gap-2 shrink-0">
        <button 
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors
            ${isDark 
              ? 'bg-slate-700 hover:bg-slate-600 text-white'
              : 'bg-slate-100 hover:bg-slate-200 text-slate-800'}`
          }
        >{settings.declineButtonText}</button>
        <button className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors">
          {settings.acceptButtonText}
        </button>
      </div>
    </div>
  );
};

const GdprSettings: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [domain, setDomain] = useState<Domain | undefined>(undefined);
  const [settings, setSettings] = useState<GdprSettingsType>(DEFAULT_SETTINGS);
  const [enabled, setEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const { t } = useLanguage();

  // State for modals
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [showLinkBuilder, setShowLinkBuilder] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false); 
  const [lbSource, setLbSource] = useState('');
  const [lbCopied, setLbCopied] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const domainData = await getDomainById(id);
        if (domainData) {
          setDomain(domainData);
          setSettings(domainData.gdprSettings || DEFAULT_SETTINGS);
          setEnabled(domainData.gdprConsentEnabled || false);
        } else {
          setError('Domain not found.');
        }
      } catch (err) {
        setError('Failed to load domain data.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id]);
  
  const handleSettingChange = (field: keyof GdprSettingsType, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!id) return;
    setIsSaving(true);
    try {
      await updateDomain(id, { 
        gdprSettings: settings,
        gdprConsentEnabled: enabled 
      });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (err) {
      alert('Failed to save settings.');
    } finally {
      setIsSaving(false);
    }
  };
  
  const sidebarContent = id ? (
    <nav className="space-y-1">
      <Link
        to={`/report/${id}`}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
      >
        <Share2 size={20} />
        {t.analytics}
      </Link>
      <Link
        to={`/report/${id}/gdpr-settings`}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-semibold transition-colors"
      >
        <ShieldCheck size={20} />
        {t.gdprSettings}
      </Link>
      <Link
        to={`/report/${id}/accessibility`}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
      >
        <Accessibility size={20} />
        {t.accessibility}
      </Link>
      <button onClick={() => setShowShareModal(true)} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
        <Globe size={20} />
        {t.shareReport}
      </button>
      <button onClick={() => setShowLinkBuilder(true)} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
        <LinkIcon size={20} />
        {t.linkBuilder}
      </button>
      <button onClick={() => setShowEmbedModal(true)} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
        <Code size={20} />
        {t.installCode}
      </button>
    </nav>
  ) : null;


  if (isLoading) {
    return (
      <Layout sidebarContent={sidebarContent}>
        <div className="flex h-full items-center justify-center">
          <Loader2 className="animate-spin text-indigo-500" size={32} />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout sidebarContent={sidebarContent}>
        <div className="text-center text-red-500">{error}</div>
      </Layout>
    );
  }

  return (
    <Layout sidebarContent={sidebarContent}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center rounded-xl shadow-sm">
              <ShieldCheck size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">GDPR Banner</h1>
              <p className="text-slate-500 dark:text-slate-400">Consent management for {domain?.name}</p>
            </div>
          </div>
          
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-8 py-3 bg-indigo-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/20 disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="animate-spin" size={20} /> : (showSuccess ? <Check size={20} /> : <Save size={20} />)}
            {showSuccess ? 'Saved!' : 'Save Changes'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* --- SETTINGS PANEL --- */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Main Toggle Card */}
            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
               <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${enabled ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                    <ShieldCheck size={20} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white">Enable Consent Banner</h3>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Privacy Control</p>
                  </div>
                </div>
                <button
                  onClick={() => setEnabled(!enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${enabled ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            <div className={`bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm space-y-6 transition-opacity duration-300 ${!enabled ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
               <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-b border-slate-50 dark:border-slate-800 pb-2">Appearance Settings</h3>
               
               {/* Position */}
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Position</label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {(['bottom-left', 'bottom-center', 'bottom-right'] as const).map(pos => (
                    <button 
                      key={pos}
                      onClick={() => handleSettingChange('position', pos)}
                      className={`p-3 rounded-lg border-2 transition-colors ${settings.position === pos ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500' : 'bg-slate-50 dark:bg-slate-800 border-transparent hover:border-slate-300 dark:hover:border-slate-600'}`}
                    >
                      {pos === 'bottom-left' && <AlignLeft className="mx-auto" />}
                      {pos === 'bottom-center' && <AlignCenter className="mx-auto" />}
                      {pos === 'bottom-right' && <AlignRight className="mx-auto" />}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Style */}
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Style</label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {(['light', 'dark'] as const).map(style => (
                    <button 
                      key={style}
                      onClick={() => handleSettingChange('style', style)}
                      className={`p-3 rounded-lg border-2 flex items-center justify-center gap-2 capitalize transition-colors ${settings.style === style ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500' : 'bg-slate-50 dark:bg-slate-800 border-transparent hover:border-slate-300 dark:hover:border-slate-600'}`}
                    >
                      {style === 'light' ? <Sun size={16} /> : <Moon size={16} />} {style}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message Text */}
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Message Text</label>
                <textarea
                  value={settings.messageText}
                  onChange={e => handleSettingChange('messageText', e.target.value)}
                  rows={3}
                  className="w-full mt-2 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-1 focus:ring-indigo-500 outline-none text-sm transition-all"
                />
              </div>

              {/* Button Labels */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Accept Btn</label>
                  <input
                    type="text"
                    value={settings.acceptButtonText}
                    onChange={e => handleSettingChange('acceptButtonText', e.target.value)}
                    className="w-full mt-2 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-1 focus:ring-indigo-500 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Decline Btn</label>
                  <input
                    type="text"
                    value={settings.declineButtonText}
                    onChange={e => handleSettingChange('declineButtonText', e.target.value)}
                    className="w-full mt-2 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-1 focus:ring-indigo-500 outline-none text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* --- PREVIEW PANEL --- */}
          <div className="lg:col-span-2 bg-slate-100 dark:bg-slate-950 p-6 rounded-[32px] border-2 border-dashed border-slate-200 dark:border-slate-800 relative min-h-[500px] flex flex-col justify-center items-center">
             <div className="absolute top-6 left-8 flex items-center gap-2 text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                Live Preview
             </div>
              {!enabled && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-slate-100/40 dark:bg-slate-950/40 backdrop-blur-[1px]">
                   <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 text-slate-400 flex items-center justify-center rounded-xl">
                        <EyeOff size={20} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-700 dark:text-slate-300">Banner Disabled</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Tracking starts instantly</p>
                      </div>
                   </div>
                </div>
              )}
              <div className="w-full h-full relative">
                <div className="w-4/5 h-2/3 bg-white dark:bg-slate-900 rounded-3xl shadow-lg mx-auto my-auto relative overflow-hidden border border-slate-100 dark:border-slate-800">
                  <div className="h-10 bg-slate-50 dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700 flex items-center px-4 gap-2">
                    <div className="w-2 h-2 rounded-full bg-rose-400"></div>
                    <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                    <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                  </div>
                  <div className="p-8 space-y-4">
                    <div className="h-6 w-1/3 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse"></div>
                    <div className="h-4 w-full bg-slate-50 dark:bg-slate-800/50 rounded-lg animate-pulse"></div>
                    <div className="h-4 w-5/6 bg-slate-50 dark:bg-slate-800/50 rounded-lg animate-pulse"></div>
                    <div className="h-4 w-1/2 bg-slate-50 dark:bg-slate-800/50 rounded-lg animate-pulse"></div>
                    <div className="grid grid-cols-3 gap-4 pt-4">
                       <div className="h-24 bg-slate-50 dark:bg-slate-800 rounded-2xl"></div>
                       <div className="h-24 bg-slate-50 dark:bg-slate-800 rounded-2xl"></div>
                       <div className="h-24 bg-slate-50 dark:bg-slate-800 rounded-2xl"></div>
                    </div>
                  </div>
                </div>
                <GdprBannerPreview settings={settings} enabled={enabled} />
              </div>
          </div>
        </div>
      </div>
      
      {/* Modals */}
      {showLinkBuilder && domain && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-slate-100 dark:border-slate-800">
             <div className="flex justify-between items-start mb-6">
               <h3 className="text-xl font-bold text-slate-900 dark:text-white">{t.linkBuilderTitle}</h3>
               <button onClick={() => setShowLinkBuilder(false)}><X size={24} className="text-slate-400" /></button>
             </div>
             <div className="space-y-4">
               <div>
                 <label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400 tracking-wider">{t.sourceName}</label>
                 <input 
                   className="w-full mt-2 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:border-indigo-500 text-slate-900 dark:text-white"
                   placeholder="e.g. facebook"
                   value={lbSource}
                   onChange={(e) => setLbSource(e.target.value)}
                 />
                 <div className="flex flex-wrap gap-2 mt-3">
                    {['Facebook', 'Google', 'Instagram', 'Yelp', 'TikTok'].map(s => (
                       <button
                          key={s}
                          onClick={() => setLbSource(s.toLowerCase())}
                          className="px-3 py-1.5 text-xs font-medium bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-900/20 dark:hover:text-indigo-400 transition-colors"
                       >
                          {s}
                       </button>
                    ))}
                 </div>
               </div>
               <div className="p-4 bg-slate-900 dark:bg-black rounded-xl text-indigo-200 font-mono text-xs break-all relative">
                  {domain.url}/?s={lbSource.trim() || 'source'}
                  <button 
                    onClick={() => { navigator.clipboard.writeText(`${domain.url}/?s=${lbSource.trim()}`); setLbCopied(true); setTimeout(() => setLbCopied(false), 2000); }}
                    className="absolute top-2 right-2 p-1.5 bg-white/10 rounded hover:bg-white/20 text-white"
                  >
                    {lbCopied ? <Check size={14} /> : <Copy size={14} />}
                  </button>
               </div>
             </div>
          </div>
        </div>
      )}

      {showShareModal && domain && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-slate-100 dark:border-slate-800">
             <div className="flex justify-between items-start mb-6">
               <h3 className="text-xl font-bold text-slate-900 dark:text-white">{t.shareReportTitle}</h3>
               <button onClick={() => setShowShareModal(false)}><X size={24} className="text-slate-400" /></button>
             </div>
             <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl text-indigo-700 dark:text-indigo-300 font-mono text-xs break-all relative">
                  {window.location.origin}/#/public/{domain.id}
                  <button 
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/#/public/${domain.id}`); setShareCopied(true); setTimeout(() => setShareCopied(false), 2000); }}
                    className="absolute top-2 right-2 p-1.5 bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200 rounded-lg"
                  >
                    {shareCopied ? <Check size={14} /> : <Copy size={14} />}
                  </button>
             </div>
          </div>
        </div>
      )}

      {showEmbedModal && domain && <EmbedModal domain={domain} onClose={() => setShowEmbedModal(false)} />}
    </Layout>
  );
};

export default GdprSettings;
